import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import Fade from '@mui/material/Fade';
import GridListComponent from '../../../grid/GridList';
import ObjectiveCard from './ObjectiveCard';
import ObjectiveFilter from './ObjectiveFilter';
import { calculateCardSize } from '../../../../utils/CardSizeCalculator';
import { useObjectCardFilter } from './ObjectiveCardListDetailHooks';
import CardDetailContainer, { useCardDetail } from '../CardDetailContainer';
import ObjectiveDetail from './ObjectiveDetail';
import { filterUndefined } from '../../../../utils/FilterUndefinedObject';
import './ObjectiveCardListDetail.scss';

const cardSize = calculateCardSize();
const ObjectiveCardListDetail = forwardRef(({
  ...props
}, ref) => {
  const defaultProps = {
    masterObjectiveData: {
      masterObjectives: [],
      included: [],
      objectiveFilterValue: {},
    },
    multiSelectFilterConfig: [],
    onFilterChange: () => { },
    currentUser: {},
    filterChildren: null,
    editGoalNameAction: () => { },
    createCardDetailAction: () => ([]),
    editMasterySetAndConsecutiveLessonAction: () => { },
    onLessonSelect: null,
  };
  const {
    masterObjectiveData,
    multiSelectFilterConfig,
    onFilterChange,
    currentUser,
    filterChildren,
    editGoalNameAction,
    createCardDetailAction,
    editMasterySetAndConsecutiveLessonAction,
    onLessonSelect,
  } = { ...defaultProps, ...filterUndefined(props) };
  const { included } = masterObjectiveData;
  const {
    filteredObjectives,
    filterValue,
    handleOnFilterChange,
  } = useObjectCardFilter(masterObjectiveData, multiSelectFilterConfig, onFilterChange);

  const {
    handleOnViewDetailClick,
    handleOnCloseDetail,
    openCardDetail,
    setOpenCardDetail,
  } = useCardDetail();

  useImperativeHandle(ref, () => ({
    closeObjectiveCardDetail: () => {
      setOpenCardDetail({
        open: false,
        selectedIndex: null,
        rowIndex: null,
      });
    },
    getCurrentOpenObjective: () => filteredObjectives[openCardDetail.rowIndex].objectives[openCardDetail.selectedIndex],
  }));

  const objectiveList = useMemo(() => (
    <>
      <ObjectiveFilter
        filterValue={filterValue}
        onFilterChange={handleOnFilterChange}
        multiSelectFilterConfig={multiSelectFilterConfig}
      >
        {filterChildren}
      </ObjectiveFilter>
      {filteredObjectives && filteredObjectives.length > 0 ? (
        filteredObjectives.map((objectivesWithTitle, rowIndex) => (
          objectivesWithTitle.objectives.length > 0
            ? (
              <GridListComponent
                key={`objective-list-${objectivesWithTitle.objectiveRowTitle}-${JSON.stringify(filterValue)}`}
                titleName={objectivesWithTitle.objectiveRowTitle}
                // slidesToShow={cardSize.slidesToShow - 1}
                slidesToScroll={cardSize.slidesToScroll - 1}
                gradient={false}
                data-test={`lesson-list-row-${objectivesWithTitle.objectiveRowTitle}`}
              >
                {
                  objectivesWithTitle.objectives.map((objective, cardIndex) => (
                    <ObjectiveCard
                      key={`object-${objective.id}`}
                      masterObjectiveData={objective}
                      included={included}
                      width={cardSize.width}
                      onClick={() => handleOnViewDetailClick(cardIndex, rowIndex)}
                    />
                  ))
                }
              </GridListComponent>
            ) : null
        ))
      ) : null}
    </>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [filteredObjectives]);

  const cardSlide = useRef(null);

  const objectiveDetail = () => (
    openCardDetail.open ? (
      <CardDetailContainer
        ref={cardSlide}
        className='objective-detail-card-container'
        open={openCardDetail.open}
        selectedIndex={openCardDetail.selectedIndex}
        onClose={handleOnCloseDetail}
        dataList={filteredObjectives[openCardDetail.rowIndex].objectives}
        onPageChange={(newIndex) => {
          setOpenCardDetail({
            ...openCardDetail,
            selectedIndex: newIndex,
          });
        }}
        createCardFunction={(objective, index) => (
          <ObjectiveDetail
            inert={openCardDetail.selectedIndex !== index}
            key={`objective-detail-${objective.id}`}
            objective={objective}
            currentUser={currentUser}
            included={masterObjectiveData.included}
            editMasterySetAndConsecutiveLessonAction={
              objective.meta.archived ? null
                : editMasterySetAndConsecutiveLessonAction(objective, openCardDetail.rowIndex, (cardIndex) => {
                  cardSlide.current.goToSlide(cardIndex, true);
                }, (newRowIndex) => {
                  setOpenCardDetail({
                    ...openCardDetail,
                    selectedIndex: 0,
                    rowIndex: newRowIndex,
                  });
                })
            }
            editGoalNameAction={objective.meta.archived ? null
              : editGoalNameAction(objective, openCardDetail.rowIndex,
                (cardIndex) => {
                  cardSlide.current.goToSlide(cardIndex, true);
                })}
            actions={createCardDetailAction(objective, openCardDetail.rowIndex, () => {
              handleOnCloseDetail();
            }, masterObjectiveData.included)}
            onLessonSelect={onLessonSelect}
          />
        )}
      />
    ) : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  );

  return (
    <Fade in>
      <div>
        {objectiveList}
        {objectiveDetail()}
      </div>
    </Fade>
  );
});

export default ObjectiveCardListDetail;
